// ABOUTME: REST hook for the active cart (an offer with status 'cart') and its mutations.
// ABOUTME: Wraps the /api/cpq/cart endpoints plus line-item edits against the cart offer.

import { useCallback, useState } from 'react';

import { OfferDetail } from './useOffer';

interface ApiResponse<T> {
    success: boolean;
    data?: T;
    error?: string;
    message?: string;
}

async function apiFetch<T>(url: string, options?: RequestInit): Promise<T> {
    const response = await fetch(url, {
        headers: { 'Content-Type': 'application/json' },
        ...options,
    });
    const json: ApiResponse<T> = await response.json();
    if (!json.success) {
        throw new Error(json.error ?? 'Unknown error');
    }
    return json.data as T;
}

export interface ConvertCartPayload {
    title: string;
    customerName: string;
    customerEmail?: string;
}

export interface AddToCartOptions {
    configurationJson?: string;
    prototypeId?: string;
    modelId?: string;
}

/**
 * Hook for active-cart operations via REST API.
 */
export function useCart() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const getActiveCart = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            return await apiFetch<OfferDetail | null>('/api/cpq/cart');
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to load cart';
            setError(message);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const addToCart = useCallback(
        async (productId: string, quantity = 1, options?: AddToCartOptions) => {
            setLoading(true);
            setError(null);
            try {
                return await apiFetch<{ offerId: string; lineItemId: string }>(
                    '/api/cpq/cart/items',
                    {
                        method: 'POST',
                        body: JSON.stringify({ productId, quantity, ...options }),
                    },
                );
            } catch (err) {
                const message = err instanceof Error ? err.message : 'Failed to add to cart';
                setError(message);
                throw err;
            } finally {
                setLoading(false);
            }
        },
        [],
    );

    const discardCart = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            await apiFetch('/api/cpq/cart', { method: 'DELETE' });
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to discard cart';
            setError(message);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const convertCart = useCallback(async (payload: ConvertCartPayload) => {
        setLoading(true);
        setError(null);
        try {
            return await apiFetch<{ offerId: string; offerNumber: string }>(
                '/api/cpq/cart/convert',
                {
                    method: 'POST',
                    body: JSON.stringify(payload),
                },
            );
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to convert cart';
            setError(message);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const updateCartItem = useCallback(
        async (cartId: string, lineItemId: string, payload: { quantity?: number }) => {
            setLoading(true);
            setError(null);
            try {
                await apiFetch(`/api/cpq/offers/${cartId}/line-items/${lineItemId}`, {
                    method: 'PUT',
                    body: JSON.stringify(payload),
                });
            } catch (err) {
                const message = err instanceof Error ? err.message : 'Failed to update item';
                setError(message);
                throw err;
            } finally {
                setLoading(false);
            }
        },
        [],
    );

    const removeCartItem = useCallback(async (cartId: string, lineItemId: string) => {
        setLoading(true);
        setError(null);
        try {
            await apiFetch(`/api/cpq/offers/${cartId}/line-items/${lineItemId}`, {
                method: 'DELETE',
            });
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to remove item';
            setError(message);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    return {
        loading,
        error,
        getActiveCart,
        addToCart,
        discardCart,
        convertCart,
        updateCartItem,
        removeCartItem,
    };
}
